import { useMemo } from 'react';
import {
  Play,
  CheckCircle,
  Unplug,
  FolderOpen,
  CalendarCheck,
  FileText,
} from 'lucide-react';
import { getChannelIcon } from '@/utils/channels';
import type { CalendarEvent, Channel } from '@/services/business/calendar';
import type { AutoValidatedVideo } from '@/stores/calendarStore';
import type { TaskCategory, TaskItem } from '@/components/TaskDigest';
import type { NavigateFunction } from 'react-router-dom';

interface UseCalendarDigestCategoriesOptions {
  events: CalendarEvent[];
  autoValidatedVideos: AutoValidatedVideo[];
  isKnowledgeBaseComplete: boolean;
  disconnectedNetworks: string[];
  videosWithPendingResources: Array<{
    id: string;
    title: string | null;
    earliestPublicationDate: Date | null;
  }>;
  hasUserAssetsAccess: boolean;
  isMobile: boolean;
  navigate: NavigateFunction;
  // Stable setState dispatchers — intentionally omitted from deps (useState guarantees stability)
  setShowNetworkSidePanel: (v: boolean) => void;
  setShowKnowledgeWizard: (v: boolean) => void;
  setShowNetworkPanel: (v: string | null) => void;
  setSelectedEvent: (v: CalendarEvent | null) => void;
  setSelectedResourceVideo: (v: { id: string; title: string } | null) => void;
  setShowResourcesPanel: (v: boolean) => void;
  setEditingContent: (v: CalendarEvent | null) => void;
  setScheduleModalOpen: (v: boolean) => void;
}

export function useCalendarDigestCategories({
  events,
  autoValidatedVideos,
  isKnowledgeBaseComplete,
  disconnectedNetworks,
  videosWithPendingResources,
  hasUserAssetsAccess,
  isMobile,
  navigate,
  setShowNetworkSidePanel,
  setShowKnowledgeWizard,
  setShowNetworkPanel,
  setSelectedEvent,
  setSelectedResourceVideo,
  setShowResourcesPanel,
  setEditingContent,
  setScheduleModalOpen,
}: UseCalendarDigestCategoriesOptions): TaskCategory[] {
  return useMemo((): TaskCategory[] => {
    const categories: TaskCategory[] = [];

    if (!isKnowledgeBaseComplete) {
      const editorialEvents = events.filter((e) => e.type === 'editorial');
      if (editorialEvents.length > 0) {
        const sortedEditorial = [...editorialEvents].sort(
          (a, b) => a.date.getTime() - b.date.getTime(),
        );
        const closestDeadline = new Date(sortedEditorial[0].date);
        closestDeadline.setDate(closestDeadline.getDate() - 1);

        categories.push({
          id: 'kb',
          label: 'Base de connaissance',
          icon: FileText,
          count: editorialEvents.length,
          onClick: () => {
            setShowNetworkSidePanel(false);
            setShowKnowledgeWizard(true);
          },
          nextDeadline: closestDeadline,
        });
      }
    }

    const allNeededNetworks = new Set<string>();
    let closestNetworkDeadline: Date | undefined;
    events.forEach((e) => {
      if (e.type === 'content' && e.channels && !e.isDraft) {
        const hasDisconnected = e.channels.some((ch) =>
          disconnectedNetworks.includes(ch),
        );
        if (hasDisconnected) {
          e.channels.forEach((ch) => {
            if (disconnectedNetworks.includes(ch)) {
              allNeededNetworks.add(ch);
            }
          });
          const deadline = new Date(e.date);
          deadline.setHours(deadline.getHours() - 1);
          if (!closestNetworkDeadline || deadline < closestNetworkDeadline) {
            closestNetworkDeadline = deadline;
          }
        }

        if (e.isMultihook && disconnectedNetworks.includes('youtube')) {
          allNeededNetworks.add('youtube');
          const ytDeadline = new Date(e.date);
          ytDeadline.setHours(ytDeadline.getHours() - 48);
          if (!closestNetworkDeadline || ytDeadline < closestNetworkDeadline) {
            closestNetworkDeadline = ytDeadline;
          }
        }
      }
    });

    if (allNeededNetworks.size > 0) {
      const networkLabels: Record<string, string> = {
        instagram: 'Instagram',
        tiktok: 'TikTok',
        youtube: 'YouTube',
        linkedin: 'LinkedIn',
        facebook: 'Facebook',
      };
      const networkItems: TaskItem[] = Array.from(allNeededNetworks).map((network) => ({
        id: network,
        label: networkLabels[network] || network,
        icon: getChannelIcon(network as Channel),
        onClick: () => {
          const firstContentEvent = events.find(
            (e) =>
              e.type === 'content' && e.channels?.includes(network as Channel),
          );
          if (firstContentEvent) {
            setShowNetworkPanel(firstContentEvent.id);
          }
        },
      }));

      categories.push({
        id: 'networks',
        label: 'Réseaux à connecter',
        icon: Unplug,
        count: allNeededNetworks.size,
        onClick: () => {},
        nextDeadline: closestNetworkDeadline,
        expandable: true,
        items: networkItems,
        isNetworkCategory: true,
      });
    }

    const videosToValidateMap = new Map<string, CalendarEvent>();
    events.forEach((e) => {
      if (e.type === 'content' && e.contentStatus === 'to_validate' && !e.isDraft) {
        const existing = videosToValidateMap.get(e.id);
        if (!existing || e.date < existing.date) {
          videosToValidateMap.set(e.id, e);
        }
      }
    });
    const videosToValidate = Array.from(videosToValidateMap.values());

    if (videosToValidate.length > 0) {
      const sortedVideos = [...videosToValidate].sort(
        (a, b) => a.date.getTime() - b.date.getTime(),
      );
      const closestValidateDeadline = new Date(sortedVideos[0].date);
      closestValidateDeadline.setHours(closestValidateDeadline.getHours() - 1);

      const videoItems: TaskItem[] = sortedVideos.map((video) => {
        const itemDeadline = new Date(video.date);
        itemDeadline.setHours(itemDeadline.getHours() - 1);
        return {
          id: `validate-${video.id}`,
          label: video.title || 'Vidéo sans titre',
          icon: Play,
          onClick: () => setSelectedEvent(video),
          deadline: itemDeadline,
        };
      });

      categories.push({
        id: 'validate',
        label: 'Vidéos à valider',
        icon: CheckCircle,
        count: videosToValidate.length,
        onClick: () => {},
        nextDeadline: closestValidateDeadline,
        expandable: true,
        items: videoItems,
      });
    }

    if (hasUserAssetsAccess && videosWithPendingResources.length > 0) {
      const sortedResources = [...videosWithPendingResources].sort((a, b) => {
        if (!a.earliestPublicationDate && !b.earliestPublicationDate) return 0;
        if (!a.earliestPublicationDate) return 1;
        if (!b.earliestPublicationDate) return -1;
        return (
          a.earliestPublicationDate.getTime() - b.earliestPublicationDate.getTime()
        );
      });

      const resourceItems: TaskItem[] = sortedResources.map((video) => ({
        id: `resources-${video.id}`,
        label: video.title || 'Vidéo sans titre',
        icon: FolderOpen,
        onClick: () => {
          if (!isMobile) {
            setSelectedResourceVideo({
              id: video.id,
              title: video.title || 'Vidéo sans titre',
            });
            setShowResourcesPanel(true);
          } else {
            navigate(`/app/user-assets?videoId=${video.id}`);
          }
        },
        deadline: video.earliestPublicationDate || undefined,
      }));

      const closestResourceDeadline =
        sortedResources[0]?.earliestPublicationDate || undefined;

      categories.push({
        id: 'resources',
        label: 'Ressources en attente',
        icon: FolderOpen,
        count: videosWithPendingResources.length,
        onClick: () => {
          if (!isMobile && sortedResources.length > 0) {
            setSelectedResourceVideo({
              id: sortedResources[0].id,
              title: sortedResources[0].title || 'Vidéo sans titre',
            });
            setShowResourcesPanel(true);
          } else {
            navigate('/app/user-assets');
          }
        },
        nextDeadline: closestResourceDeadline,
        expandable: true,
        items: resourceItems,
      });
    }

    if (autoValidatedVideos.length > 0) {
      const rescheduleItems = autoValidatedVideos.map((video: AutoValidatedVideo) => ({
        id: `reschedule-${video.id}`,
        label: video.title || 'Vidéo sans titre',
        icon: CalendarCheck,
        onClick: () => {
          const fakeEvent: CalendarEvent = {
            id: video.id,
            date: new Date(),
            type: 'content',
            title: video.title || 'Vidéo sans titre',
            coverUrl: video.video_1_thumbnail || undefined,
            contentStatus: 'auto_validated',
          };
          setEditingContent(fakeEvent);
          setScheduleModalOpen(true);
        },
      }));

      categories.push({
        id: 'reschedule',
        label: 'À reprogrammer',
        icon: CalendarCheck,
        count: autoValidatedVideos.length,
        onClick: () => {},
        expandable: true,
        items: rescheduleItems,
      });
    }

    return categories;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    events,
    autoValidatedVideos,
    isKnowledgeBaseComplete,
    disconnectedNetworks,
    videosWithPendingResources,
    hasUserAssetsAccess,
    navigate,
    // setState dispatchers are stable (useState guarantees) — not listed to match original behavior
  ]);
}
